// Asıl üretim sayfası — metin yapıştır → voice seç → üret → MP3 indir.
// Karia Audio.tsx pattern'i sadeleştirilmiş + maliyet preview eklenmiş.

import { useEffect, useRef, useState } from 'react';
import { Sparkles, Loader2, Download, Play, Pause, RotateCcw, AlertTriangle } from 'lucide-react';
import { supabase, type VoiceProfile } from '../lib/supabase';
import { generateAudio, GenerateError, fetchUsage, type UsageInfo } from '../lib/elevenlabs';
import { useAuth } from '../lib/auth-context';

const MAX_CHARS = 8000;
const WARN_CHARS = 6000;
const DANGER_CHARS = 7500;

export default function Studio() {
  const auth = useAuth();
  const [voices, setVoices] = useState<VoiceProfile[]>([]);
  const [voiceSlug, setVoiceSlug] = useState<string>('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ url: string; bytes: number; chars: number; cost: number } | null>(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Kalan kredi kotası — SADECE master görür (editör maliyet/kota görmez).
  const [usage, setUsage] = useState<UsageInfo | null>(null);

  useEffect(() => {
    void supabase
      .from('voice_profiles')
      .select('*')
      .eq('is_active', true)
      .order('display_order')
      .then(({ data }) => {
        const list = (data ?? []) as VoiceProfile[];
        setVoices(list);
        if (list.length > 0 && !voiceSlug) setVoiceSlug(list[0]!.slug);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Master ise kalan kredi kotasını çek (editör için çalışmaz).
  useEffect(() => {
    if (!auth.isMaster) return;
    void fetchUsage().then(setUsage).catch(() => { /* kota alınamazsa sessizce gizle */ });
  }, [auth.isMaster]);

  const chars = text.length;

  async function handleGenerate() {
    if (!text.trim() || !voiceSlug) return;
    setBusy(true);
    setError(null);
    setResult(null);
    stopAudio();
    try {
      const out = await generateAudio({ text, voiceSlug });
      setResult({
        url: out.url,
        bytes: out.bytes,
        chars: out.chars,
        cost: out.estimated_cost_usd,
      });
    } catch (e) {
      if (e instanceof GenerateError) {
        if (e.code === 'voice_not_configured') {
          setError('Bu ses henüz kalibre edilmedi (PLACEHOLDER). Master admin Sesler sayfasından gerçek voice ID girmeli.');
        } else if (e.code === 'daily_limit_exceeded') {
          setError('Bugünkü 16.700 karakter limitin doldu. Master override için Fikret\'e bildirin.');
        } else {
          setError(`${e.code} — ${e.detail ?? 'bilinmeyen hata'}`);
        }
      } else {
        setError((e as Error).message);
      }
    } finally {
      setBusy(false);
    }
  }

  function stopAudio() {
    audioRef.current?.pause();
    if (audioRef.current) audioRef.current.currentTime = 0;
    audioRef.current = null;
    setPlaying(false);
  }

  function togglePlay() {
    if (!result?.url) return;
    if (!audioRef.current) {
      audioRef.current = new window.Audio(result.url);
      audioRef.current.onended = () => setPlaying(false);
    }
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      void audioRef.current.play();
      setPlaying(true);
    }
  }

  // Supabase Storage farklı origin olduğu için <a download> ve blob yöntemleri
  // bazı tarayıcılarda/mobilde sesi açıp oynatıyordu. Çözüm: Storage'ın
  // ?download= parametresi → sunucu "Content-Disposition: attachment" döndürür,
  // böylece düz link tıklaması garantili indirir (cross-origin/mobil dahil).
  function downloadUrl(url: string): string {
    const name = `seslendirme-${Date.now()}.mp3`;
    return url + (url.includes('?') ? '&' : '?') + 'download=' + encodeURIComponent(name);
  }

  function handleNew() {
    stopAudio();
    setText('');
    setResult(null);
    setError(null);
  }

  const selectedVoice = voices.find((v) => v.slug === voiceSlug);
  const charsColor =
    chars >= DANGER_CHARS ? 'text-red-600' :
    chars >= WARN_CHARS ? 'text-amber-600' :
    'text-slate-500';

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl">
      <h1 className="text-2xl font-bold text-slate-800 mb-1">🎙️ Stüdyo</h1>
      <p className="text-slate-500 text-sm mb-6">Metni yapıştır, ses seç, üret, indir.</p>

      {/* Kalan kredi kotası — sadece master görür */}
      {auth.isMaster && usage && (
        <div className="mb-6 flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-600">
          <span className="font-semibold text-slate-700">ElevenLabs kotası:</span>
          <span className="font-mono">
            {usage.remaining.toLocaleString('tr-TR')} / {usage.character_limit.toLocaleString('tr-TR')} kredi kalan
          </span>
          {usage.tier && <span className="text-slate-400">· {usage.tier} plan</span>}
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 space-y-4">
        {/* Voice picker */}
        <div>
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2 block">Ses</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {voices.length === 0 && (
              <p className="text-sm text-slate-400 col-span-full">Henüz ses tanımlı değil. Master admin Sesler sayfasından eklemeli.</p>
            )}
            {voices.map((v) => (
              <button
                key={v.id}
                onClick={() => setVoiceSlug(v.slug)}
                disabled={busy}
                className={`text-left p-3 rounded-lg border transition-colors ${
                  voiceSlug === v.slug
                    ? 'border-[color:var(--color-primary)] bg-[color:var(--color-primary)]/5'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="font-medium text-sm text-slate-800">{v.display_name}</div>
                {v.description && <div className="text-xs text-slate-500 mt-1 line-clamp-2">{v.description}</div>}
                {v.elevenlabs_voice_id.startsWith('PLACEHOLDER_') && (
                  <div className="text-xs text-amber-600 mt-1 inline-flex items-center gap-1">
                    <AlertTriangle size={12} /> Kalibre edilmedi
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Textarea */}
        <div>
          <div className="flex items-baseline justify-between mb-2">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Metin</label>
            <div className={`text-xs font-mono ${charsColor}`}>
              {chars.toLocaleString('tr-TR')} / {MAX_CHARS.toLocaleString('tr-TR')} karakter
            </div>
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, MAX_CHARS))}
            disabled={busy}
            rows={12}
            placeholder="Seslendirilecek metni buraya yapıştır…"
            className="w-full border border-slate-300 rounded-lg p-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[color:var(--color-primary)] resize-y"
          />
        </div>

        {/* Action */}
        <div className="flex items-center justify-between">
          <button
            onClick={handleNew}
            disabled={busy || (!text && !result)}
            className="text-sm text-slate-500 hover:text-slate-700 inline-flex items-center gap-1 disabled:opacity-30"
          >
            <RotateCcw size={14} /> Temizle
          </button>
          <button
            onClick={handleGenerate}
            disabled={busy || !text.trim() || !voiceSlug || (selectedVoice?.elevenlabs_voice_id.startsWith('PLACEHOLDER_') ?? false)}
            className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-soft)] disabled:opacity-40 text-white px-5 py-2.5 rounded-lg font-medium inline-flex items-center gap-2 shadow-sm"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {busy ? 'Üretiliyor…' : 'Üret'}
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="font-medium text-emerald-800">Hazır ✓</p>
                <p className="text-xs text-emerald-700 mt-0.5">
                  {result.chars.toLocaleString('tr-TR')} karakter · {(result.bytes / 1024).toFixed(1)} KB
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={togglePlay}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-emerald-300 rounded text-sm text-emerald-800 hover:bg-emerald-100"
                >
                  {playing ? <Pause size={14} /> : <Play size={14} />}
                  {playing ? 'Durdur' : 'Dinle'}
                </button>
                <a
                  href={downloadUrl(result.url)}
                  download={`seslendirme-${result.chars}c.mp3`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm font-medium"
                >
                  <Download size={14} /> İndir
                </a>
              </div>
            </div>
            <p className="text-xs text-emerald-700/70 font-mono break-all">{result.url}</p>
          </div>
        )}
      </div>
    </div>
  );
}
